import React, { useState } from "react";
import { Box, CircularProgress, Typography } from "@material-ui/core";
import { useParams } from "react-router-dom";
import LessonEditor from "./LessonEditor";
import { useGetLesson } from "routes/createContent/api/useGetLesson";
import { LessonFormData, emptyPart } from "../types";
import { getErrorMessage } from "../utils";
import { ActiveLocaleProvider } from "../ActiveLocaleContext";

const emptyLesson: LessonFormData = {
  target_joints: [],
  parts: [emptyPart()],
};

const LessonEditorPage: React.VFC = () => {
  const { id } = useParams<{ id?: string }>();
  const lessonId = id ? parseInt(id, 10) : null;
  const { data, isLoading, isError, error } = useGetLesson(lessonId);
  // Bumped after each successful save so the editor remounts against the
  // freshly-refetched lesson data. Remounting is the cleanest way to resync
  // all of RHF's nested useFieldArray instances with the saved values.
  const [reloadKey, setReloadKey] = useState(0);

  if (lessonId && isLoading) {
    return (
      <Box p={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }
  if (lessonId && isError) {
    return (
      <Box p={4}>
        <Typography color="error">Failed to load lesson: {getErrorMessage(error)}</Typography>
      </Box>
    );
  }

  const { rollout, ...loaded } = data ?? {};
  const initial: LessonFormData = lessonId && data ? (loaded as LessonFormData) : emptyLesson;
  return (
    // ActiveLocaleProvider lives above LessonEditor so the active-locale
    // selection survives the save-triggered remount below.
    <ActiveLocaleProvider>
      <LessonEditor
        key={reloadKey}
        initialValues={initial}
        rollout={rollout}
        onSaved={() => setReloadKey(k => k + 1)}
      />
    </ActiveLocaleProvider>
  );
};

export default LessonEditorPage;
